import React from "react";
import styled from "styled-components";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { RotateLoader } from "@lib/components";
import { api } from "../api";

const DEFAULT_BG_IMAGE =
	"https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1200&auto=format&fit=crop&q=80";

const Shell = styled.div`
	min-height: 100vh;
	min-height: 100dvh;
	display: flex;
`;

const LeftPanel = styled.div`
	display: none;
	position: relative;
	overflow: hidden;

	@media (min-width: 1024px) {
		display: flex;
		width: 50%;
	}
`;

const LeftImage = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const LeftOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: rgba(15, 23, 42, 0.3);
`;

const LeftFooter = styled.div`
	position: absolute;
	bottom: 24px;
	left: 24px;
	color: white;
	font-size: 14px;
	opacity: 0.75;
	z-index: 1;
`;

const RightPanel = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 48px 24px;
	background: white;
	min-height: 100vh;
	min-height: 100dvh;
`;

const Content = styled.div`
	width: 100%;
	max-width: 384px;
`;

const LogoHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 32px;

	img {
		height: 40px;
		width: auto;
		max-height: 40px;
		object-fit: contain;
	}
`;

const LogoTitle = styled.div`
	font-size: 20px;
	font-weight: 700;
	color: #1e293b;
	line-height: 1.2;
`;

const LogoSubtitle = styled.div`
	font-size: 12px;
	color: #94a3b8;
	margin-top: -2px;
`;

const AuthHeading = styled.h1`
	font-size: 24px;
	font-weight: 700;
	color: #0f172a;
	margin: 0 0 4px;
`;

const AuthSubheading = styled.p`
	font-size: 14px;
	color: #64748b;
	margin: 0 0 24px;
`;

const FormField = styled.div`
	margin-bottom: 16px;
`;

const FormLabel = styled.label`
	display: block;
	font-size: 14px;
	font-weight: 500;
	color: #334155;
	margin-bottom: 4px;
`;

const FormInput = styled.input`
	width: 100%;
	padding: 8px 12px;
	font-size: 14px;
	color: #0f172a;
	background: white;
	border: 1px solid ${(props) => props.theme.colors.primary};
	border-radius: 6px;
	transition: border-color 0.15s, box-shadow 0.15s;
	box-sizing: border-box;

	&::placeholder {
		color: #94a3b8;
	}

	&:focus {
		outline: none;
		border-color: ${(props) => props.theme.colors.primary};
		box-shadow: 0 0 0 2px ${(props) => props.theme.colors.primary}33;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const PasswordField = styled.div`
	position: relative;
	width: 100%;

	${FormInput} {
		padding-right: 40px;
	}
`;

const PasswordToggle = styled.button`
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #94a3b8;

	&:hover {
		color: #475569;
	}
`;

const LegalText = styled.p`
	margin: 0 0 16px;
	font-size: 14px;
	line-height: 1.5;
	color: #64748b;

	a {
		color: ${(props) => props.theme.colors.primary};
		text-decoration: underline;
		font-weight: normal;

		&:hover {
			opacity: 0.85;
		}
	}
`;

const SubmitButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	margin-top: 16px;
	padding: 10px 16px;
	font-size: 16px;
	font-weight: 500;
	color: white;
	background: ${(props) => props.theme.colors.primary};
	border: none;
	border-radius: 6px;
	cursor: pointer;
	transition: opacity 0.15s;

	&:hover:not(:disabled) {
		opacity: 0.9;
	}

	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px white, 0 0 0 4px ${(props) => props.theme.colors.primary};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

interface Props {
	setLoggedIn: (v: boolean) => void;
	masterEmail: (v: string) => void;
}

export function ViewLogin({ setLoggedIn, masterEmail }: Props) {
	const [loading, setLoading] = React.useState(false);
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [passwordVisible, setPasswordVisible] = React.useState(false);
	const currentYear = new Date().getFullYear();

	return (
		<Shell>
			<LeftPanel>
				<LeftImage src={DEFAULT_BG_IMAGE} alt="" />
				<LeftOverlay />
				<LeftFooter>&copy; {currentYear} CloudWaitress</LeftFooter>
			</LeftPanel>

			<RightPanel>
				<Content>
					<LogoHeader>
						<img src="/logo.png" alt="CloudWaitress logo" />
						<div>
							<LogoTitle>CloudWaitress</LogoTitle>
							<LogoSubtitle>Reseller Dashboard</LogoSubtitle>
						</div>
					</LogoHeader>

					<AuthHeading>Welcome back</AuthHeading>
					<AuthSubheading>Sign in to your account to continue.</AuthSubheading>

					<form
						onSubmit={async (e) => {
							e.preventDefault();
							try {
								setLoading(true);
								const result = await api.login({ email, password });
								if (result.outcome) {
									alert(result.message);
								} else {
									api.token = result.token;
									masterEmail(email);
									setLoggedIn(true);
								}
							} catch (err) {
								console.log(err);
								alert("Something went wrong, please try again or contact us");
							} finally {
								setLoading(false);
							}
						}}
					>
						<FormField>
							<FormLabel htmlFor="email">Email address</FormLabel>
							<FormInput
								id="email"
								type="email"
								name="email"
								value={email}
								placeholder="you@example.com"
								onChange={(e) => setEmail(e.target.value)}
								autoComplete="email"
								required
								disabled={loading}
							/>
						</FormField>

						<FormField>
							<FormLabel htmlFor="password">Password</FormLabel>
							<PasswordField>
								<FormInput
									id="password"
									type={passwordVisible ? "text" : "password"}
									name="password"
									value={password}
									placeholder="••••••••"
									onChange={(e) => setPassword(e.target.value)}
									autoComplete="current-password"
									required
									disabled={loading}
								/>
								<PasswordToggle
									type="button"
									onClick={() => setPasswordVisible(!passwordVisible)}
									aria-label={passwordVisible ? "Hide password" : "Show password"}
								>
									{passwordVisible ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
								</PasswordToggle>
							</PasswordField>
						</FormField>

						<LegalText>
							By logging in, you are reminded that you are bound by the{" "}
							<a href="/termsandconditions" target="_blank" rel="noopener noreferrer">
								Terms and Conditions
							</a>
							; and that you accept the{" "}
							<a href="/privacypolicy" target="_blank" rel="noopener noreferrer">
								Privacy Policy
							</a>
							.
						</LegalText>

						<SubmitButton type="submit" disabled={loading}>
							{loading ? <RotateLoader size={2} /> : "Login"}
						</SubmitButton>
					</form>
				</Content>
			</RightPanel>
		</Shell>
	);
}
